import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["as", "children", "className", "dangerDescription", "disabled", "hasIconOnly", "href", "iconDescription", "isExpressive", "isSelected", "kind", "onBlur", "onClick", "onFocus", "onMouseEnter", "onMouseLeave", "renderIcon", "size", "tabIndex", "type"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import cx from 'classnames';
import { usePrefix } from '../../internal/usePrefix.js';
import { useId } from '../../internal/useId.js';
var ButtonBase = /*#__PURE__*/React.forwardRef(function ButtonBase(_ref, ref) {
  var _cx;
  var as = _ref.as,
    children = _ref.children,
    className = _ref.className,
    _ref$dangerDescriptio = _ref.dangerDescription,
    dangerDescription = _ref$dangerDescriptio === void 0 ? 'danger' : _ref$dangerDescriptio,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$hasIconOnly = _ref.hasIconOnly,
    hasIconOnly = _ref$hasIconOnly === void 0 ? false : _ref$hasIconOnly,
    href = _ref.href,
    iconDescription = _ref.iconDescription,
    _ref$isExpressive = _ref.isExpressive,
    isExpressive = _ref$isExpressive === void 0 ? false : _ref$isExpressive,
    isSelected = _ref.isSelected,
    _ref$kind = _ref.kind,
    kind = _ref$kind === void 0 ? 'primary' : _ref$kind,
    onBlur = _ref.onBlur,
    onClick = _ref.onClick,
    onFocus = _ref.onFocus,
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    ButtonImageElement = _ref.renderIcon,
    size = _ref.size,
    tabIndex = _ref.tabIndex,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var buttonClasses = cx(className, (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--btn"), true), _defineProperty(_cx, "".concat(prefix, "--btn--sm"), size === 'sm' && !isExpressive), _defineProperty(_cx, "".concat(prefix, "--btn--md"), size === 'md' && !isExpressive), _defineProperty(_cx, "".concat(prefix, "--btn--lg"), size === 'lg' && !isExpressive), _defineProperty(_cx, "".concat(prefix, "--btn--xl"), size === 'xl'), _defineProperty(_cx, "".concat(prefix, "--btn--2xl"), size === '2xl'), _defineProperty(_cx, "".concat(prefix, "--layout--size-").concat(size), size), _defineProperty(_cx, "".concat(prefix, "--btn--").concat(kind), kind), _defineProperty(_cx, "".concat(prefix, "--btn--disabled"), disabled), _defineProperty(_cx, "".concat(prefix, "--btn--expressive"), isExpressive), _defineProperty(_cx, "".concat(prefix, "--btn--icon-only"), hasIconOnly), _defineProperty(_cx, "".concat(prefix, "--btn--selected"), hasIconOnly && isSelected && kind === 'ghost'), _cx));
  var commonProps = {
    tabIndex: tabIndex,
    className: buttonClasses,
    ref: ref
  };
  var buttonImage = !ButtonImageElement ? null : /*#__PURE__*/React.createElement(ButtonImageElement, {
    "aria-label": iconDescription,
    className: "".concat(prefix, "--btn__icon"),
    "aria-hidden": "true"
  });
  var dangerButtonVariants = ['danger', 'danger--tertiary', 'danger--ghost'];
  var component = 'button';
  var assistiveId = useId('danger-description');
  var ariaPressed = rest['aria-pressed'],
    ariaDescribedBy = rest['aria-describedby'];
  var otherProps = {
    disabled: disabled,
    type: type,
    'aria-describedby': dangerButtonVariants.includes(kind) ? assistiveId : ariaDescribedBy || undefined,
    'aria-pressed': ariaPressed !== null && ariaPressed !== void 0 ? ariaPressed : hasIconOnly && kind === 'ghost' ? isSelected : undefined
  };
  var anchorProps = {
    href: href
  };
  var assistiveText = null;
  if (dangerButtonVariants.includes(kind)) {
    assistiveText = /*#__PURE__*/React.createElement("span", {
      id: assistiveId,
      className: "".concat(prefix, "--visually-hidden")
    }, dangerDescription);
  }
  if (as) {
    component = as;
    otherProps = _objectSpread(_objectSpread({}, otherProps), anchorProps);
  } else if (href && !disabled) {
    component = 'a';
    otherProps = anchorProps;
  }
  return /*#__PURE__*/React.createElement(component, _objectSpread(_objectSpread(_objectSpread({
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    onFocus: onFocus,
    onBlur: onBlur,
    onClick: onClick
  }, rest), commonProps), otherProps), assistiveText, children, buttonImage);
});
export { ButtonBase as default };